import { FunctionComponent } from "react";

import { faArrowRight, faCheck, faTrash, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Toggle } from "@/components/Toggle/Toggle";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { InputField } from "@/components/InputField/InputField";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import {
	FieldMappingAiConfig,
	FieldMappingSourceType,
	SalesforceField,
	SalesforceFieldMapping,
	SalesforceSourceField,
} from "../schema/salesforce";
import {
	AiHintBanner,
	AiSourceCell,
	CannotMapRow,
	SourceTypePills,
	TargetFieldBadges,
} from "./fieldMapping";
import { isUnwritableField } from "./salesforceFieldMapping.utils";

type Ts = (key: string, options?: Record<string, unknown>) => string;

const buildBadgeLabels = (ts: Ts) => ({
	required: ts("metaBadge.required"),
	multi: ts("metaBadge.multi"),
	dropdown: ts("metaBadge.dropdown"),
	number: ts("metaBadge.number"),
	calculated: ts("metaBadge.calculated"),
	readOnly: ts("metaBadge.readOnly"),
	restricted: ts("metaBadge.restricted"),
});

/**
 * What the badges read from. `isRestricted` is Salesforce's `restrictedPicklist` and is the
 * only flag that means "closed set" — an unrestricted picklist happily stores a new value, so
 * showing every picklist as restricted would be a lie the AI-fallback validation then inherits.
 */
const toBadgeField = (field: SalesforceField | undefined) => ({
	...(field ?? {}),
	isRestricted: field?.optionsAreRestricted,
});

interface MappingRowProps {
	mapping: SalesforceFieldMapping;
	sourceFields: SalesforceSourceField[];
	salesforceFields: SalesforceField[];
	onToggleActive: (id: number, isActive: boolean) => void;
	onDelete: (id: number) => void;
	onConfigureAi: (mapping: SalesforceFieldMapping) => void;
	ts: Ts;
}

export const SalesforceMappingRow: FunctionComponent<MappingRowProps> = ({
	mapping,
	sourceFields,
	salesforceFields,
	onToggleActive,
	onDelete,
	onConfigureAi,
	ts,
}) => {
	const isConstant = mapping.sourceType === FieldMappingSourceType.CONSTANT;
	const isAi = mapping.sourceType === FieldMappingSourceType.AI;
	const salesforceField = salesforceFields.find((field) => field.name === mapping.salesforceField);
	const targetLabel = salesforceField
		? `${salesforceField.label}${salesforceField.isCustom ? " (Custom)" : ""}`
		: (mapping.salesforceFieldLabel ?? mapping.salesforceField);
	const badgeLabels = buildBadgeLabels(ts);

	if (salesforceField && isUnwritableField(salesforceField)) {
		return (
			<CannotMapRow
				badgeLabels={badgeLabels}
				cannotMapLabel={ts("cannotMap")}
				targetField={toBadgeField(salesforceField)}
				targetFieldLabel={targetLabel}
				testIdPrefix="salesforce"
				tooltip={ts("cannotMapTooltip")}
				onDelete={() => onDelete(mapping.id)}
			/>
		);
	}

	const sourceLabel = isConstant
		? ts("constantLabel", { value: mapping.sourceField })
		: (sourceFields.find((field) => field.name === mapping.sourceField)?.label ??
			mapping.sourceField);

	return (
		<div className="flex items-center gap-3 border-b border-border-light py-3">
			<div className="flex-1">
				{isAi ? (
					<AiSourceCell
						badgeLabel={ts("aiBadge")}
						configureLabel={ts("aiConfigure")}
						creditTooltip={ts("aiCreditTooltip")}
						label={targetLabel}
						testIdPrefix="salesforce"
						onConfigure={() => onConfigureAi(mapping)}
					/>
				) : (
					<div
						className={classNames(
							"rounded-xl border border-border-light bg-brand-white px-3 py-[9px] text-ssm text-text",
							isConstant && "text-text-meta italic"
						)}
						data-testid={isConstant ? "salesforce-constant-label" : "salesforce-field-label"}
					>
						{sourceLabel}
					</div>
				)}
			</div>
			<div className="flex w-[24px] items-center justify-center">
				<Icon className="h-[16px] w-[16px] text-text-meta" icon={faArrowRight} />
			</div>
			<div className="flex-1">
				<div className="flex items-center gap-2 rounded-xl border border-border-light bg-brand-white px-3 py-[9px] text-ssm text-text">
					<span className="flex-1 truncate">{targetLabel}</span>
					<TargetFieldBadges
						field={toBadgeField(salesforceField)}
						labels={badgeLabels}
						testIdPrefix="salesforce"
					/>
				</div>
			</div>
			<div className="flex w-[60px] items-center justify-center">
				<Toggle
					isChecked={mapping.isActive}
					onChange={() => onToggleActive(mapping.id, !mapping.isActive)}
				/>
			</div>
			<div
				className="group w-fit cursor-pointer rounded border-border-light bg-status-error-light p-1 hover:border-text-status-error"
				onClick={() => onDelete(mapping.id)}
			>
				<Icon className="h-4 w-4 cursor-pointer text-status-error" icon={faTrash} />
			</div>
		</div>
	);
};

export interface NewSalesforceMapping {
	tempId: string;
	sourceField: string;
	sourceType: FieldMappingSourceType;
	aiConfig?: FieldMappingAiConfig;
	salesforceField: string;
	isActive: boolean;
	entityType: string;
}

interface NewMappingRowProps {
	mapping: NewSalesforceMapping;
	sourceFields: SalesforceSourceField[];
	salesforceFields: SalesforceField[];
	usedSourceFields: string[];
	usedTargetFieldsByType: Record<FieldMappingSourceType, string[]>;
	onUpdate: (tempId: string, data: Partial<NewSalesforceMapping>) => void;
	onSave: (mapping: NewSalesforceMapping) => void;
	onCancel: (tempId: string) => void;
	onConfigureAi: (mapping: NewSalesforceMapping) => void;
	isSaving: boolean;
	ts: Ts;
}

export const SalesforceNewMappingRow: FunctionComponent<NewMappingRowProps> = ({
	mapping,
	sourceFields,
	salesforceFields,
	usedSourceFields,
	usedTargetFieldsByType,
	onUpdate,
	onSave,
	onCancel,
	onConfigureAi,
	isSaving,
	ts,
}) => {
	const isConstant = mapping.sourceType === FieldMappingSourceType.CONSTANT;
	const isAi = mapping.sourceType === FieldMappingSourceType.AI;
	const usedTargetFields = usedTargetFieldsByType[mapping.sourceType] ?? [];

	const availableSourceFields = sourceFields.filter(
		(field) => field.name === mapping.sourceField || !usedSourceFields.includes(field.name)
	);
	const availableTargetFields = salesforceFields.filter(
		(field) =>
			(field.name === mapping.salesforceField || !usedTargetFields.includes(field.name)) &&
			!isUnwritableField(field)
	);

	const sourceOptions: AutoCompleteItem[] = availableSourceFields.map((field) => ({
		id: field.name,
		title: field.label,
	}));

	const targetOptions: AutoCompleteItem[] = availableTargetFields.map((field) => ({
		id: field.name,
		title: `${field.label}${field.isCustom ? " (Custom)" : ""}`,
	}));

	const selectedSource = sourceOptions.find((option) => option.id === mapping.sourceField);
	const selectedTarget = targetOptions.find((option) => option.id === mapping.salesforceField);
	const selectedTargetField = salesforceFields.find(
		(field) => field.name === mapping.salesforceField
	);
	const canSave = isAi
		? Boolean(mapping.salesforceField && mapping.aiConfig?.prompt)
		: Boolean(mapping.sourceField) && Boolean(mapping.salesforceField);
	const saveDisabledTooltip = isAi && !mapping.aiConfig?.prompt ? ts("aiSaveDisabledTooltip") : "";
	const badgeLabels = buildBadgeLabels(ts);

	const handleSourceTypeChange = (next: FieldMappingSourceType) => {
		if (next === mapping.sourceType) {
			return;
		}

		onUpdate(mapping.tempId, { sourceType: next, sourceField: "", aiConfig: undefined });
	};

	return (
		<div className="flex flex-col gap-2 rounded-lg border-b border-border-light bg-brand-white px-2 py-3">
			<div className="flex items-start gap-3">
				<div className="flex flex-1 flex-col gap-2">
					<SourceTypePills
						labels={{
							fromRecord: ts("sourceTypeFromRecord"),
							constant: ts("sourceTypeConstant"),
							ai: ts("sourceTypeAi"),
						}}
						testIdPrefix="salesforce"
						value={mapping.sourceType}
						onChange={handleSourceTypeChange}
					/>
					{isAi ? (
						<AiSourceCell
							badgeLabel={ts("aiBadge")}
							configureDisabled={!mapping.salesforceField}
							configureLabel={ts("aiConfigure")}
							creditTooltip={ts("aiCreditTooltip")}
							label={selectedTarget?.title || ""}
							placeholder={ts("aiTargetPlaceholder")}
							testIdPrefix="salesforce"
							onConfigure={() => onConfigureAi(mapping)}
						/>
					) : isConstant ? (
						<InputField
							handleChange={(event) =>
								onUpdate(mapping.tempId, { sourceField: event.target.value })
							}
							maxLength={255}
							name={`salesforce-constant-${mapping.tempId}`}
							placeholder={ts("constantPlaceholder")}
							testId="salesforce-constant-input"
							value={mapping.sourceField}
						/>
					) : (
						<AutoComplete
							data={sourceOptions}
							handleReset={() => onUpdate(mapping.tempId, { sourceField: "" })}
							handleSearch={() => {}}
							handleSelect={(item) => {
								if (item) {
									onUpdate(mapping.tempId, { sourceField: item.id as string });
								}
							}}
							placeholder={ts("selectField")}
							selectedItem={selectedSource?.title || ""}
						/>
					)}
				</div>
				<div className="flex w-[24px] items-center justify-center pt-9">
					<Icon className="h-[16px] w-[16px] text-text-meta" icon={faArrowRight} />
				</div>
				<div className="flex flex-1 flex-col gap-1 pt-9">
					<AutoComplete
						data={targetOptions}
						handleReset={() =>
							onUpdate(mapping.tempId, {
								salesforceField: "",
								sourceField: isAi ? "" : mapping.sourceField,
							})
						}
						handleSearch={() => {}}
						handleSelect={(item) => {
							if (item) {
								onUpdate(mapping.tempId, {
									salesforceField: item.id as string,
									sourceField: isAi ? item.title : mapping.sourceField,
								});
							}
						}}
						placeholder={ts("selectField")}
						selectedItem={selectedTarget?.title || ""}
					/>
					{selectedTargetField && (
						<TargetFieldBadges
							field={toBadgeField(selectedTargetField)}
							labels={badgeLabels}
							testIdPrefix="salesforce-new"
						/>
					)}
				</div>
				<div className="flex w-[60px] items-center justify-center pt-9">
					<Toggle
						isChecked={mapping.isActive}
						onChange={() => onUpdate(mapping.tempId, { isActive: !mapping.isActive })}
					/>
				</div>
				<div className="flex w-[80px] gap-2 pt-9">
					<Tooltip content={saveDisabledTooltip} disabled={!saveDisabledTooltip}>
						<div className="w-fit">
							<Button
								color={ButtonColor.ACTION}
								disabled={!canSave}
								image={
									<Icon className="h-4 min-h-4 w-4 min-w-4 shrink-0 text-white" icon={faCheck} />
								}
								isLoading={isSaving}
								size={ButtonSize.XS}
								testId="salesforce-save-new-mapping"
								title=""
								onClick={() => onSave(mapping)}
							/>
						</div>
					</Tooltip>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="h-4 min-h-4 w-4 min-w-4 shrink-0 text-text" icon={faXmark} />}
							size={ButtonSize.XS}
							testId="salesforce-cancel-new-mapping"
							title=""
							onClick={() => onCancel(mapping.tempId)}
						/>
					</div>
				</div>
			</div>
			{isAi && (
				<AiHintBanner body={ts("aiHintBody")} testIdPrefix="salesforce" title={ts("aiHintTitle")} />
			)}
		</div>
	);
};
